import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Paper,
  Stack,
  useTheme,
  Typography,
  Box,
  List,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { getCompletedReportPdf, updateServiceTicket, getServiceTicketById, approveServiceTicket } from "features/service/service.slice";
import api from "app/api";
import AuthorizedPDF from "component/AuthorizedPDF";
import { Links } from "utils/pageLinkNames";
import moment from "moment";
import { toast } from "react-toastify";
import { ErrorOutline } from "@mui/icons-material";

const ViewReports = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedPdfIndex, setSelectedPdfIndex] = useState(0);
  const { completedReportPdf, isLoading, serviceTicketById, appConstants } = useSelector((state) => state.service);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [hideProgress, setHideProgress] = useState(true);
  const downloadProgressRef = useRef(0);

  useEffect(() => {
    dispatch(getServiceTicketById(id));
    dispatch(getCompletedReportPdf(id)).then(() => {});
  }, [dispatch, id]);

  const handleEdit = () => {
    navigate(`/${Links.UPDATE_SERVICE_TICKET}/${id}`, { state: { enableEditing: true } });
  };

  const handleApprove = () => {
    setShowApproveDialog(true);
  };

  const onPressConfirmApprove = () => {
    dispatch(approveServiceTicket(id)).then((updateResponse) => {
      if (!updateResponse) toast.error("Something went wrong!");
      if (updateResponse && updateResponse.error) toast.error("Please try again!");
      if (updateResponse && updateResponse.payload.data && updateResponse.payload.status === 200) {
        dispatch(getCompletedReportPdf(id));
        dispatch(getServiceTicketById(id));
        toast.success("Ticket approved successfully!", {
          autoClose: 1000,
        });
      } else toast.error("Something went wrong!");
    });
    setShowApproveDialog(false);
  };

  const handleDownload = async (pdfPath) => {
    try {
      setHideProgress(false);
      downloadProgressRef.current = 0;

      const response = await api.get(`/serviceapi/writeImageToClient?location=${pdfPath}`, {
        responseType: "blob",
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            downloadProgressRef.current = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (downloadProgressRef.current % 10 === 0) {
              setDownloadProgress(downloadProgressRef.current);
            }
          }
        },
      });

      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", pdfPath.split("/").pop());
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      setTimeout(() => {
        setHideProgress(true);
        setDownloadProgress(0);
      }, 1000);
    } catch (error) {
      console.error("Download failed", error);
      setHideProgress(true);
    }
  };

  return (
    <Stack flexDirection="row" pr={2}>
      {isLoading ? (
        <Box>
          <div
            style={{
              textAlign: "center",
              paddingTop: 200,
            }}
          >
            <CircularProgress color="secondary" />
          </div>
        </Box>
      ) : (
        <>
          <List
            component="nav"
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              backgroundColor: "white",
              marginLeft: "30px",
              height: 910,
              overflowY: "scroll",
            }}
          >
            {completedReportPdf?.map(({ report, status }, index) => (
              <ListItemButton
                key={index}
                onClick={() => setSelectedPdfIndex(index)}
                selected={selectedPdfIndex === index}
                sx={{
                  boxShadow: "0 0 5px rgba(0, 0, 0, 0.2)",
                  borderRadius: "8px",
                  marginBottom: "10px",
                  backgroundColor: selectedPdfIndex === index ? theme.palette.primary.main : "#f5f5f5",
                }}
              >
                <ListItemText
                  primary={
                    report
                      .split("/")
                      .pop()
                      .split("svcTktId")
                      .slice(1)
                      .join("service ticketid")
                      .replace(/_/g, " ")
                      .replace(/(\d+)(\D+)(\d+)(\D+)(\d+)(\D+)(\d+)/, "$1 - $2 $3 $4 $5,$6$7")
                      .trim()
                      .replace(/\s+/g, " ")
                      .replace(/\s\d+\.pdf$/, "")
                      .replace(/(\d{2})\s(\d{2})$/, "$1$2") +
                    " - " +
                    status
                  }
                  sx={{ color: "black", wordBreak: "break-all" }}
                />
              </ListItemButton>
            ))}
          </List>

          <Box ml={10} mt={2}>
            <Paper elevation={8} sx={{ width: 750, padding: 1 }}>
              {!isLoading && !serviceTicketById?.status && (
                <Card sx={{ m: 3, p: 2 }}>
                  <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <ErrorOutline sx={{ fontSize: 40, color: "error.dark", mb: 1 }} />
                    <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "error.dark", mb: 0.5 }}>
                      Ticket Not Found
                    </Typography>
                    <Typography variant="body1" sx={{ color: "error.dark" }}>
                      The ticket ID "{id || ""}" is invalid or does not exist.
                    </Typography>
                  </CardContent>
                </Card>
              )}
              <AuthorizedPDF path={completedReportPdf[selectedPdfIndex]?.report} scale={1} showPages={true} />
            </Paper>
          </Box>
          {serviceTicketById?.status && user?.roles?.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR) && (
            <Box ml={10} mt={5}>
              <Stack direction="column" spacing={3}>
                {serviceTicketById?.status === appConstants.allTypes.SERVICE_TICKET_STATUS.SUPERVISOR_APPROVED ? (
                  <Button onClick={handleEdit} color="primary" variant="contained" sx={{ width: "150px" }}>
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button onClick={handleApprove} color="primary" variant="contained" sx={{ width: "150px" }}>
                      Approve
                    </Button>
                    <Button onClick={handleEdit} color="primary" variant="contained" sx={{ width: "150px" }}>
                      Edit
                    </Button>
                  </>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "150px" }}>
                  <Button
                    onClick={() => handleDownload(completedReportPdf[selectedPdfIndex]?.report)}
                    color="primary"
                    variant="contained"
                    sx={{ width: "150px" }}
                    disabled={!hideProgress}
                  >
                    Download
                  </Button>
                  {!hideProgress && (
                    <Box sx={{ width: "100%", mt: 0, padding: 0 }}>
                      <LinearProgress variant="determinate" value={downloadProgress} sx={{ width: "100%" }} />
                      <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", mt: 0.3 }}>
                        {`${downloadProgress.toFixed(2)}%`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Box>
          )}
        </>
      )}

      <Dialog open={showApproveDialog} onClose={() => setShowApproveDialog(false)}>
        <DialogContent>
          <Typography mt={1}>
            Click <span style={{ fontWeight: "bold" }}> "Confirm" </span> to approve the ticket.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApproveDialog(false)} variant="contained">
            Cancel
          </Button>
          <Button variant="contained" onClick={onPressConfirmApprove}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ViewReports;
